import api from 'api';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import actions from 'store/actions';
import { AxiosResponse } from 'axios';
import './cipManagement.scss';
import CipThreshold from './components/CipThreshold';
import { Button } from 'components/Base';
import { MetaType } from 'store/modules/Meta/types';
import LoaderSpinner from 'components/LoaderSpinner/LoaderSpinner';
import NewCipThresholdModal from 'components/Forms/CIP/NewCipThreshold';
import { errHelper, Notify } from 'utils';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolderOutlined';
import selectors from 'store/selectors';
import ReadOnlyAlert from 'components/Base/Alert/ReadOnlyAlert';
import Routes from 'consts/routes';
import { useHistory } from 'react-router-dom';

type CipManagementV2Props = {
  getCipCategories: Function;
  getMpsFirms: Function;
  updateCashFund: Function;
  updateAssetClasses: Function;
  isFirmAdmin: boolean;
};

const CipManagementV2: React.FC<CipManagementV2Props> = ({
  getCipCategories,
  getMpsFirms,
  updateCashFund,
  updateAssetClasses,
  isFirmAdmin
}) => {
  const history = useHistory();
  const [cipThresholds, setCipThresholds] = useState([] as any[]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    setIsLoading(true);
    api.getCashFund().then(data => {
      updateCashFund(data);
    });
    api.getCipThresholds().then((cip_thresholds: AxiosResponse) => {
      setCipThresholds(cip_thresholds.data);
      api.getAssetClasses().then(response => {
        updateAssetClasses(response);
        setIsLoading(false);
      });
    });

    getCipCategories();
    getMpsFirms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addCipThreshold = (cip: MetaType) => {
    setCipThresholds([...cipThresholds, cip]);
  };

  const onCipThresholdSave = (cip: MetaType, index: number) => {
    const cips = cipThresholds;
    cips[index] = cip;
    setCipThresholds([...cips]);
  };

  const saveCipThreshold = (cip: MetaType, index: number) => {
    setIsSaving(true);

    const id = Number(cip.value) === 0 ? null : Number(cip.value);
    api
      .createCipThreshold({ id, name: cip.name })
      .then(({ data }: AxiosResponse) => {
        id ? onCipThresholdSave(data, index) : addCipThreshold(data);
        Notify.success(
          `You have sucessfully ${id ? 'updated' : 'created'} ${cip.name}`
        );
      })
      .catch(err => errHelper(err))
      .finally(() => {
        setIsSaving(false);
        setIsModalOpen(false);
      });
  };

  const onDeleteCipThreshold = (index: number) => {
    const cips = cipThresholds;
    cips.splice(index, 1);
    setCipThresholds([...cips]);
  };

  return (
    <div
      className={
        cipThresholds.length === 0
          ? 'cipThresholdContainer flex'
          : 'cipThresholdContainer'
      }
    >
      {isLoading ? (
        <LoaderSpinner isLoading={isLoading} />
      ) : (
        <>
          {cipThresholds.length === 0 ? (
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <CreateNewFolderIcon
                htmlColor="#6b7280"
                style={{ width: '36px', height: '36px' }}
              />
              <div style={{ fontWeight: 600 }}>No Investment Options</div>
              <div style={{ color: '#6b7280' }}>
                {isFirmAdmin
                  ? 'Start building your Central Investment Proposition by creating a new Investment Option.'
                  : 'Contact your firm administrator to create a new Investment Option.'}
              </div>
            </div>
          ) : (
            <>
              {!isFirmAdmin && (
                <div style={{ marginBottom: '1rem' }}>
                  <ReadOnlyAlert />
                </div>
              )}
            </>
          )}
          {!(!isFirmAdmin && cipThresholds.length > 1) && (
            <Button
              type="submit"
              onClick={() =>
                isFirmAdmin
                  ? setIsModalOpen(true)
                  : history.push(Routes.HOMEPAGE)
              }
              style={{ marginBottom: '1rem' }}
            >
              {isFirmAdmin
                ? 'Add New Investment Option'
                : 'Go back to Dashboard'}
            </Button>
          )}
          {cipThresholds.map((cip, i) => {
            return (
              <CipThreshold
                key={cip.name}
                cip={cip}
                index={i}
                onCipThresholdSave={saveCipThreshold}
                onDeleteCipThreshold={onDeleteCipThreshold}
              />
            );
          })}
          <NewCipThresholdModal
            isModalOpen={isModalOpen}
            isSaving={isSaving}
            onSubmit={(values: any) => saveCipThreshold(values, 0)}
            onClose={() => setIsModalOpen(false)}
          />
        </>
      )}
    </div>
  );
};

export default connect(
  (state: any) => ({
    isFirmAdmin: selectors.advisorSelectors.isFirmAdmin(state)
  }),
  {
    getCipCategories: actions.cipActions.getCipCategories,
    getMpsFirms: actions.cipActions.getMpsFirms,
    updateCashFund: actions.metaActions.updateCashFund,
    updateAssetClasses: actions.metaActions.updateAssetClasses
  }
)(CipManagementV2);
